import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { supabase, SUPABASE_PROJECT_ID, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import { trackEvent } from '@/lib/analytics';
import { useScoringRules } from '@/contexts/ScoringRulesContext';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Settings2,
  Save,
  Trophy,
  Bomb,
  DollarSign,
  Gavel,
  BarChart3,
  Users,
  Calendar,
  Check,
  Info,
  Zap,
  Clock,
  CheckCircle2,
  AlertCircle,
  Wallet,
  ShieldCheck,
  Building2,
  Crown,
  Lock as LockIcon,
  ClipboardList,
  ShieldAlert as ShieldAlertIcon,
  ArrowLeftRight,
  ChevronRight,
  ArrowLeft,
} from 'lucide-react';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { DEFAULT_MLB_TRASH_SCORING, MLB_SCORING_CATEGORIES } from '@/data/mlbScoringData';
// mlbDefaultSabotages removed — matchup sabotages are now fixed for beta
import { ScoringRule } from '@/types/scoring';
// SabotageConfig import removed — matchup sabotages are now fixed for beta
import DraftSabotageSettings from '@/components/league/DraftSabotageSettings';
import CommissionerOverrides from '@/components/mlb-beta/CommissionerOverrides';
import MembersTab from '@/components/mlb-beta/MembersTab';
import { bareDateToDate } from '@/lib/etDate';

interface LeagueData {
  id: string;
  name: string;
  sport: string;
  team_count: number;
  league_type: string;
  scoring_format: string;
  scoring_type: string;
  draft_type: string;
  waiver_type: string;
  faab_budget: number;
  playoff_teams: number;
  regular_season_weeks: number;
  sabotage_enabled: boolean;
  sabotage_spin_cost: number;
  draft_sabotage_enabled: boolean;
  draft_sabotage_spin_cost: number;
  status: string;
  invite_code: string;
}

const DEFAULT_ROSTER_SIZE = 25;
const DEFAULT_ROSTER_LIMITS: Record<string, number> = {
  C: 2, '1B': 3, '2B': 3, '3B': 3, SS: 3,
  OF: 5, SP: 5, RP: 4, P: 9, DH: 1, UTIL: 2, BENCH: 6,
};
const DEFAULT_ROSTER_MINS: Record<string, number> = {
  C: 1, '1B': 1, '2B': 1, '3B': 1, SS: 1,
  OF: 1, SP: 1, RP: 1, P: 5, DH: 0, UTIL: 1, BENCH: 0,
};

const SUPER_ADMIN_EMAIL = 'adamneilemerson@gmail.com';

const MLBBetaSettings = ({ isCommissioner = false, resetKey = 0 }: { isCommissioner?: boolean; resetKey?: number }) => {
  const [league, setLeague] = useState<LeagueData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { scoringRules, setScoringRules } = useScoringRules();
  const { activeLeague: ctxLeague, refreshLeague } = useLeague();
  const { user } = useAuth();
  const isSuperAdmin = user?.email === SUPER_ADMIN_EMAIL;

  // Local settings state
  const [leagueName, setLeagueName] = useState('');
  const [teamCount, setTeamCount] = useState(8);
  const [leagueType, setLeagueType] = useState('h2h-points');
  const [scoringFormat, setScoringFormat] = useState('weekly');
  const [scoringType, setScoringType] = useState('trash');
  const [draftType, setDraftType] = useState('snake');
  const [waiverType, setWaiverType] = useState('free_agent');
  const [faabBudget, setFaabBudget] = useState(100);
  const [playoffTeams, setPlayoffTeams] = useState(4);
  const [regularSeasonWeeks, setRegularSeasonWeeks] = useState(13);
  const [sabotageEnabled, setSabotageEnabled] = useState(true);
  const [sabotageSpinCost, setSabotageSpinCost] = useState(0);
  const [draftSabotageEnabled, setDraftSabotageEnabled] = useState(true);
  const [lineupLockType, setLineupLockType] = useState<'weekly' | 'per_game'>('weekly');
  const [draftSabotageSpinCost, setDraftSabotageSpinCost] = useState(0);
  const [tradeDeadline, setTradeDeadline] = useState<string>('');
  const [maxAcquisitions, setMaxAcquisitions] = useState<number>(0);
  const [acquisitionPeriod, setAcquisitionPeriod] = useState<'weekly' | 'season'>('weekly');

  // Slow draft settings
  const [hoursPerPick, setHoursPerPick] = useState(8);
  const [pauseOvernight, setPauseOvernight] = useState(true);
  const [pauseStartHour, setPauseStartHour] = useState(23);
  const [pauseEndHour, setPauseEndHour] = useState(8);

  // Draft timing (shared with drafts table)
  const [secondsPerPick, setSecondsPerPick] = useState(90);
  const [draftRounds, setDraftRounds] = useState(15);
  const [draftRecordId, setDraftRecordId] = useState<string | null>(null);

  // Season date pickers
  const [seasonStartDate, setSeasonStartDate] = useState<Date>(new Date('2026-03-23'));
  const [regularSeasonEndDate, setRegularSeasonEndDate] = useState<Date>(new Date('2026-09-27'));
  const [playoffStartDate, setPlayoffStartDate] = useState<Date>(new Date('2026-09-29'));
  const [playoffEndDate, setPlayoffEndDate] = useState<Date>(new Date('2026-10-25'));

  // Merged week settings
  const [mergeOpeningWeeks, setMergeOpeningWeeks] = useState(true);
  const [mergeAllStarBreak, setMergeAllStarBreak] = useState(true);
  const [allStarBreakDate, setAllStarBreakDate] = useState<Date>(new Date('2026-07-14'));

  // Settings navigation
  type SettingsSection = 'league' | 'members' | 'roster' | 'scoring' | 'sabotages' | 'draft' | 'payments' | 'overrides';
  const [activeSection, setActiveSection] = useState<SettingsSection | null>(null);

  // Reset to root menu when parent signals a settings click
  useEffect(() => {
    if (resetKey > 0) setActiveSection(null);
  }, [resetKey]);

  // Roster limits state
  const [totalRosterSize, setTotalRosterSize] = useState(DEFAULT_ROSTER_SIZE);
  const [rosterLimits, setRosterLimits] = useState<Record<string, number>>({ ...DEFAULT_ROSTER_LIMITS });
  const [rosterMins, setRosterMins] = useState<Record<string, number>>({ ...DEFAULT_ROSTER_MINS });
  const [pitcherSlotType, setPitcherSlotType] = useState<'split' | 'combined'>('split');
  const [limitSpinsPerMatchup, setLimitSpinsPerMatchup] = useState(false);
  const [maxSpinsPerMatchup, setMaxSpinsPerMatchup] = useState(3);

  // Scoring rules now come from ScoringRulesContext

  // Sabotages (local state)
  // matchup sabotages are fixed (all 8 always active) — no per-sabotage state needed

  useEffect(() => {
    const fetchLeague = async () => {
      // Use the active league from LeagueContext (already membership-validated)
      if (!ctxLeague) {
        setLoading(false);
        return;
      }

      // Fetch full league record (context may not have all columns like invite_code)
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('*')
        .eq('id', ctxLeague.id)
        .maybeSingle();

      if (leagueData) {
        setLeague(leagueData as LeagueData);
        setLeagueName(leagueData.name);
        setTeamCount(leagueData.team_count);
        setLeagueType(leagueData.league_type);
        setScoringFormat(leagueData.scoring_format);
        setScoringType(leagueData.scoring_type);
        setDraftType(leagueData.draft_type);
        setWaiverType(leagueData.waiver_type);
        setFaabBudget(leagueData.faab_budget);
        setPlayoffTeams(leagueData.playoff_teams);
        setRegularSeasonWeeks(leagueData.regular_season_weeks);
        setSabotageEnabled(leagueData.sabotage_enabled);
        setSabotageSpinCost(leagueData.sabotage_spin_cost);
        setDraftSabotageEnabled(leagueData.draft_sabotage_enabled ?? true);
        setDraftSabotageSpinCost(leagueData.draft_sabotage_spin_cost ?? 15);
        setTradeDeadline((leagueData as any).trade_deadline ? (leagueData as any).trade_deadline.slice(0, 10) : '');

        // Load roster limits from category_config
        const config = leagueData.category_config as any;
        if (config?.roster_limits) {
          setRosterLimits(prev => ({ ...prev, ...config.roster_limits }));
        }
        if (config?.roster_mins) {
          setRosterMins(prev => ({ ...prev, ...config.roster_mins }));
        }
        if (config?.total_roster_size) {
          setTotalRosterSize(config.total_roster_size);
        }
        if (config?.lineup_lock_type) {
          setLineupLockType(config.lineup_lock_type);
        }
        if (config?.max_acquisitions !== undefined) {
          setMaxAcquisitions(config.max_acquisitions);
        }
        if (config?.acquisition_period) {
          setAcquisitionPeriod(config.acquisition_period);
        }
        if (config?.hours_per_pick) setHoursPerPick(config.hours_per_pick);
        if (config?.pause_overnight !== undefined) setPauseOvernight(config.pause_overnight);
        if (config?.pause_start_hour !== undefined) setPauseStartHour(config.pause_start_hour);
        if (config?.pause_end_hour !== undefined) setPauseEndHour(config.pause_end_hour);
        if (config?.season_start_date) setSeasonStartDate(bareDateToDate(config.season_start_date));
        if (config?.regular_season_end_date) setRegularSeasonEndDate(bareDateToDate(config.regular_season_end_date));
        if (config?.playoff_start_date) setPlayoffStartDate(bareDateToDate(config.playoff_start_date));
        if (config?.playoff_end_date) setPlayoffEndDate(bareDateToDate(config.playoff_end_date));
        if (config?.pitcher_slot_type) setPitcherSlotType(config.pitcher_slot_type);
        if (config?.max_spins_per_matchup !== undefined) {
          if (config.max_spins_per_matchup === null) {
            setLimitSpinsPerMatchup(false);
          } else {
            setLimitSpinsPerMatchup(true);
            setMaxSpinsPerMatchup(config.max_spins_per_matchup);
          }
        }
        if (config?.merge_opening_weeks !== undefined) setMergeOpeningWeeks(config.merge_opening_weeks);
        if (config?.merge_allstar_break !== undefined) setMergeAllStarBreak(config.merge_allstar_break);
        if (config?.allstar_break_date) setAllStarBreakDate(bareDateToDate(config.allstar_break_date));
        // matchup sabotages are now fixed (all 8 always active) — no per-sabotage config to load

        // Fetch draft record — this is the source of truth for draft-specific settings
        const { data: draftData } = await supabase
          .from('drafts')
          .select('id, draft_type, rounds, seconds_per_pick, hours_per_pick, pause_overnight, pause_start_hour, pause_end_hour')
          .eq('league_id', leagueData.id)
          .maybeSingle();

        if (draftData) {
          setDraftRecordId(draftData.id);
          if (draftData.draft_type) setDraftType(draftData.draft_type);
          if (draftData.rounds) setDraftRounds(draftData.rounds);
          if (draftData.seconds_per_pick) setSecondsPerPick(draftData.seconds_per_pick);
          if (draftData.hours_per_pick) setHoursPerPick(draftData.hours_per_pick);
          if (draftData.pause_overnight !== null && draftData.pause_overnight !== undefined) setPauseOvernight(draftData.pause_overnight);
          if (draftData.pause_start_hour !== null && draftData.pause_start_hour !== undefined) setPauseStartHour(draftData.pause_start_hour);
          if (draftData.pause_end_hour !== null && draftData.pause_end_hour !== undefined) setPauseEndHour(draftData.pause_end_hour);
        }
      }
      setLoading(false);
    };
    fetchLeague();
  }, [ctxLeague?.id, resetKey]);

  const saveLeagueSettings = async () => {
    if (!league) return;
    setSaving(true);
    // Calculate regular season weeks from dates
    const calculatedRegularSeasonWeeks = Math.max(1, Math.round((regularSeasonEndDate.getTime() - seasonStartDate.getTime()) / (7 * 24 * 60 * 60 * 1000)));
    // Auto-calculate bench spots (only count slots for current pitcher mode)
    const excludeSlots = pitcherSlotType === 'combined' ? ['BENCH', 'SP', 'RP'] : ['BENCH', 'P'];
    const activeStarters = Object.entries(rosterMins)
      .filter(([k]) => !excludeSlots.includes(k))
      .reduce((sum, [, v]) => sum + (v ?? 0), 0);
    const savedRosterMins = { ...rosterMins, BENCH: Math.max(0, totalRosterSize - activeStarters) };
    const { error, data } = await supabase
      .from('leagues')
      .update({
        name: leagueName,
        team_count: teamCount,
        league_type: leagueType,
        scoring_format: scoringFormat,
        scoring_type: scoringType,
        draft_type: draftType,
        waiver_type: waiverType,
        faab_budget: faabBudget,
        playoff_teams: playoffTeams,
        regular_season_weeks: calculatedRegularSeasonWeeks,
        sabotage_enabled: sabotageEnabled,
        sabotage_spin_cost: sabotageSpinCost,
        draft_sabotage_enabled: draftSabotageEnabled,
        draft_sabotage_spin_cost: draftSabotageSpinCost,
        category_config: JSON.parse(JSON.stringify({ ...((league as any).category_config || {}), scoring_rules: scoringRules, roster_limits: rosterLimits, roster_mins: savedRosterMins, total_roster_size: totalRosterSize, lineup_lock_type: lineupLockType, max_acquisitions: maxAcquisitions, acquisition_period: acquisitionPeriod, hours_per_pick: hoursPerPick, pause_overnight: pauseOvernight, pause_start_hour: pauseStartHour, pause_end_hour: pauseEndHour, season_start_date: format(seasonStartDate, 'yyyy-MM-dd'), regular_season_end_date: format(regularSeasonEndDate, 'yyyy-MM-dd'), playoff_start_date: format(playoffStartDate, 'yyyy-MM-dd'), playoff_end_date: format(playoffEndDate, 'yyyy-MM-dd'), merge_opening_weeks: mergeOpeningWeeks, merge_allstar_break: mergeAllStarBreak, allstar_break_date: format(allStarBreakDate, 'yyyy-MM-dd'), pitcher_slot_type: pitcherSlotType, max_spins_per_matchup: limitSpinsPerMatchup ? maxSpinsPerMatchup : null })),
        trade_deadline: tradeDeadline ? new Date(tradeDeadline + 'T23:59:59Z').toISOString() : null,
      } as any)
      .eq('id', league.id)
      .select();

    // Also sync draft settings to drafts table (single source of truth for draft config)
    if (draftRecordId) {
      await supabase
        .from('drafts')
        .update({
          draft_type: draftType,
          rounds: draftRounds,
          seconds_per_pick: secondsPerPick,
          hours_per_pick: hoursPerPick,
          pause_overnight: pauseOvernight,
          pause_start_hour: pauseStartHour,
          pause_end_hour: pauseEndHour,
        } as any)
        .eq('id', draftRecordId);
    }

    setSaving(false);
    if (error) {
      toast({ title: 'Save failed', description: error.message, variant: 'destructive' });
    } else if (!data || data.length === 0) {
      toast({ title: 'Permission denied', description: 'Only the league commissioner can update settings.', variant: 'destructive' });
    } else {
      setLeague(prev => prev ? { ...prev, name: leagueName, faab_budget: faabBudget } : prev);

      // Sync FAAB budget to all teams in the league when switching to FAAB mode
      if (waiverType === 'faab_nightly' || waiverType === 'faab_sunday') {
        await supabase
          .from('teams')
          .update({ faab_remaining: faabBudget })
          .eq('league_id', league.id);
      }

      trackEvent('commissioner_setting_changed', { setting_name: 'league_settings' });
      await refreshLeague();
      toast({ title: 'Settings saved!', description: 'League settings updated successfully.' });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 pb-20 md:pb-0">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!league) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center text-muted-foreground">
          <p className="font-medium text-foreground">No league found</p>
          <p className="text-sm mt-1">Could not load league settings.</p>
        </CardContent>
      </Card>
    );
  }

  const readOnly = !isCommissioner;

  return (
    <div className="space-y-6 pb-20 md:pb-0">
      {/* Header */}
      {activeSection ? (
        <button
          onClick={() => setActiveSection(null)}
          className="flex items-center gap-1.5 text-sm text-primary font-medium -mb-2"
        >
          <ArrowLeft className="w-4 h-4" />
          {isCommissioner ? 'Commissioner Settings' : 'League Settings'}
        </button>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold font-heading text-foreground flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-primary" />
              {isCommissioner ? 'Commissioner Settings' : 'League Settings'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {isCommissioner ? 'Manage your league configuration' : 'View your league configuration'}
            </p>
          </div>
          <Badge variant="outline" className="text-xs border-primary/40 text-primary">
            {league.invite_code}
          </Badge>
        </div>
      )}

      {/* Settings Menu */}
      {!activeSection && (
        <Card>
          <CardContent className="p-0 divide-y divide-border">
            {([
              { key: 'league' as SettingsSection, icon: Trophy, label: 'League', desc: 'Name, format, schedule' },
              { key: 'members' as SettingsSection, icon: Users, label: 'Members', desc: 'Roster & invites' },
              { key: 'roster' as SettingsSection, icon: ClipboardList, label: 'Roster', desc: 'Positions & limits' },
              { key: 'scoring' as SettingsSection, icon: BarChart3, label: 'Scoring', desc: 'Categories & point values' },
              { key: 'sabotages' as SettingsSection, icon: Bomb, label: 'Sabotage', desc: 'Matchup & draft sabotages' },
              { key: 'draft' as SettingsSection, icon: Gavel, label: 'Draft', desc: 'Type, order & timing' },
              { key: 'payments' as SettingsSection, icon: DollarSign, label: 'Funds', desc: 'Buy-in & payouts' },
              ...(isCommissioner ? [{ key: 'overrides' as SettingsSection, icon: ShieldAlertIcon, label: 'Overrides', desc: 'Manual adjustments' }] : []),
            ]).map((item) => (
              <button
                key={item.key}
                onClick={() => setActiveSection(item.key)}
                className="flex items-center gap-3 w-full px-4 py-3.5 text-left hover:bg-muted/50 transition-colors"
              >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <item.icon className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ===== Section Detail Views ===== */}

      {/* ===== LEAGUE SETTINGS ===== */}
      {activeSection === 'league' && <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">League Basics</CardTitle>
              <CardDescription>Core league configuration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>League Name</Label>
                <Input value={leagueName} onChange={(e) => setLeagueName(e.target.value)} disabled={readOnly} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Teams</Label>
                  <Select value={teamCount.toString()} onValueChange={(v) => setTeamCount(parseInt(v))} disabled={readOnly}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[4, 6, 8, 10, 12, 14, 16].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} Teams</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Playoff Teams</Label>
                  <Select value={playoffTeams.toString()} onValueChange={(v) => setPlayoffTeams(parseInt(v))} disabled={readOnly}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[2, 4, 6, 8].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n} Teams</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Season Schedule Date Pickers */}
              <div className="space-y-4">
                <Label className="text-sm font-semibold">Season Schedule</Label>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Season Start</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal text-xs h-9", !seasonStartDate && "text-muted-foreground")} disabled={readOnly}>
                          <Calendar className="mr-1.5 h-3.5 w-3.5" />
                          {format(seasonStartDate, 'MMM d, yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarPicker mode="single" selected={seasonStartDate} onSelect={(d) => d && setSeasonStartDate(d)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Regular Season End</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal text-xs h-9", !regularSeasonEndDate && "text-muted-foreground")} disabled={readOnly}>
                          <Calendar className="mr-1.5 h-3.5 w-3.5" />
                          {format(regularSeasonEndDate, 'MMM d, yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarPicker mode="single" selected={regularSeasonEndDate} onSelect={(d) => d && setRegularSeasonEndDate(d)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Playoff Start</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal text-xs h-9", !playoffStartDate && "text-muted-foreground")} disabled={readOnly}>
                          <Calendar className="mr-1.5 h-3.5 w-3.5" />
                          {format(playoffStartDate, 'MMM d, yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarPicker mode="single" selected={playoffStartDate} onSelect={(d) => d && setPlayoffStartDate(d)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">Season End</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className={cn("w-full justify-start text-left font-normal text-xs h-9", !playoffEndDate && "text-muted-foreground")} disabled={readOnly}>
                          <Calendar className="mr-1.5 h-3.5 w-3.5" />
                          {format(playoffEndDate, 'MMM d, yyyy')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarPicker mode="single" selected={playoffEndDate} onSelect={(d) => d && setPlayoffEndDate(d)} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Season summary */}
                {(() => {
                  const regWeeks = Math.max(1, Math.round((regularSeasonEndDate.getTime() - seasonStartDate.getTime()) / (7 * 24 * 60 * 60 * 1000)));
                  const playoffWeeks = Math.max(1, Math.round((playoffEndDate.getTime() - playoffStartDate.getTime()) / (7 * 24 * 60 * 60 * 1000)));
                  const teamsPerWeek = Math.floor(teamCount / 2);
                  return (
                    <div className="bg-muted/30 rounded-lg p-3 space-y-1 text-xs text-muted-foreground">
                      <p>Regular season: {format(seasonStartDate, 'MMM d')} – {format(regularSeasonEndDate, 'MMM d')} ({regWeeks} weeks, {regWeeks * teamsPerWeek} matchups)</p>
                      <p>Playoffs: {format(playoffStartDate, 'MMM d')} – {format(playoffEndDate, 'MMM d')} ({playoffWeeks} weeks)</p>
                    </div>
                  );
                })()}

                {/* Merged Weeks */}
                <div className="space-y-3 pt-2">
                  <Label className="text-sm font-semibold">Merged Weeks</Label>
                  <p className="text-[11px] text-muted-foreground">Combine short MLB calendar weeks into longer matchups for fairer scoring.</p>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-xs">Merge opening weeks</Label>
                      <p className="text-[10px] text-muted-foreground">Combine weeks 1 & 2 when the season starts mid-week</p>
                    </div>
                    <Switch checked={mergeOpeningWeeks} onCheckedChange={setMergeOpeningWeeks} disabled={readOnly} />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label className="text-xs">Merge All-Star break week</Label>
                      <p className="text-[10px] text-muted-foreground">Combine the ASB week with the following week</p>
                    </div>
                    <Switch checked={mergeAllStarBreak} onCheckedChange={setMergeAllStarBreak} disabled={readOnly} />
                  </div>

                  {mergeAllStarBreak && (
                    <div className="space-y-1.5 pl-4 border-l-2 border-primary/20">
                      <Label className="text-xs text-muted-foreground">All-Star break start date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className={cn("w-full justify-start text-left font-normal text-xs h-9")} disabled={readOnly}>
                            <Calendar className="mr-1.5 h-3.5 w-3.5" />
                            {format(allStarBreakDate, 'MMM d, yyyy')}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <CalendarPicker mode="single" selected={allStarBreakDate} onSelect={(d) => d && setAllStarBreakDate(d)} initialFocus />
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">League Type</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-3">
                {[
                  { id: 'h2h-points', name: 'H2H Points', desc: 'Cumulative weekly point totals', icon: '🏆' },
                  { id: 'h2h-cats', name: 'H2H Categories', desc: 'Win individual stat categories', icon: '📊' },
                  { id: 'roto', name: 'Rotisserie', desc: 'Season-long cumulative rankings', icon: '🔄' },
                ].map((type) => (
                  <button
                    key={type.id}
                    onClick={() => !readOnly && setLeagueType(type.id)}
                    disabled={readOnly}
                    className={cn(
                      "p-3 rounded-lg border text-left transition-all flex items-center justify-between",
                      leagueType === type.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{type.icon}</span>
                      <div>
                        <p className="font-medium text-sm">{type.name}</p>
                        <p className="text-xs text-muted-foreground">{type.desc}</p>
                      </div>
                    </div>
                    {leagueType === type.id && <Check className="w-4 h-4 text-primary" />}
                  </button>
                ))}
              </div>

              <Separator />

              <div className="space-y-2">
                <Label>Scoring Format</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { id: 'weekly', name: 'Weekly', desc: 'Full-week matchups' },
                    { id: 'daily', name: 'Daily', desc: 'Single-day slates' },
                  ].map(f => (
                    <button
                      key={f.id}
                      onClick={() => !readOnly && setScoringFormat(f.id)}
                      disabled={readOnly}
                      className={cn(
                        "p-3 rounded-lg border text-left transition-all",
                        scoringFormat === f.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                      )}
                    >
                      <p className="font-medium text-sm">{f.name}</p>
                      <p className="text-xs text-muted-foreground">{f.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Waiver Type</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'free_agent', name: 'Free Agent (FCFS)', desc: 'Instant pickups, no waiver period on drops' },
                    { id: 'faab_nightly', name: 'FAAB Nightly', desc: 'Blind FAAB bids processed daily at midnight ET' },
                    { id: 'faab_sunday', name: 'FAAB Weekly', desc: 'Blind FAAB bids processed weekly on Sunday midnight ET' },
                  ].map(w => (
                    <button
                      key={w.id}
                      onClick={() => !readOnly && setWaiverType(w.id)}
                      disabled={readOnly}
                      className={cn(
                        "p-3 rounded-lg border text-left transition-all",
                        waiverType === w.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                      )}
                    >
                      <p className="font-medium text-sm">{w.name}</p>
                      <p className="text-xs text-muted-foreground">{w.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {(waiverType === 'faab_nightly' || waiverType === 'faab_sunday') && (
                <div className="space-y-2">
                  <Label>FAAB Budget per Team</Label>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                     <Input
                      type="number"
                      value={faabBudget}
                      onChange={(e) => setFaabBudget(parseInt(e.target.value) || 100)}
                      className="w-24"
                      min={0}
                      max={1000}
                      disabled={readOnly}
                    />
                    {!readOnly && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          if (!league) return;
                          await supabase
                            .from('teams')
                            .update({ faab_remaining: faabBudget })
                            .eq('league_id', league.id);
                          await refreshLeague();
                          toast({ title: 'FAAB Reset', description: `All teams reset to $${faabBudget}.` });
                        }}
                      >
                        Reset All FAAB
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Max Acquisitions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-primary" />
                Max Acquisitions
              </CardTitle>
              <CardDescription>Limit add/drop and waiver transactions per team</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Maximum Transactions</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={maxAcquisitions}
                    onChange={(e) => setMaxAcquisitions(Math.max(0, parseInt(e.target.value) || 0))}
                    className="w-24"
                    min={0}
                    max={999}
                    disabled={readOnly}
                  />
                  <span className="text-sm text-muted-foreground">0 = unlimited</span>
                </div>
              </div>

              {maxAcquisitions > 0 && (
                <div className="space-y-2">
                  <Label>Limit Period</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { id: 'weekly' as const, name: 'Per Week', desc: 'Resets each matchup week' },
                      { id: 'season' as const, name: 'Per Season', desc: 'Total for the entire season' },
                    ].map(p => (
                      <button
                        key={p.id}
                        onClick={() => !readOnly && setAcquisitionPeriod(p.id)}
                        disabled={readOnly}
                        className={cn(
                          "p-3 rounded-lg border text-left transition-all",
                          acquisitionPeriod === p.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                        )}
                      >
                        <p className="font-medium text-sm">{p.name}</p>
                        <p className="text-xs text-muted-foreground">{p.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                {maxAcquisitions === 0
                  ? 'No limit: teams can make unlimited transactions'
                  : `Each team can make up to ${maxAcquisitions} acquisitions ${acquisitionPeriod === 'weekly' ? 'per week' : 'per season'} (add/drops + waivers)`}
              </p>
            </CardContent>
          </Card>

          {/* Trade Deadline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ArrowLeftRight className="w-5 h-5 text-primary" />
                Trade Deadline
              </CardTitle>
              <CardDescription>Set a date after which no trades can be proposed</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <Input
                  type="date"
                  value={tradeDeadline}
                  onChange={(e) => setTradeDeadline(e.target.value)}
                  className="w-48"
                  disabled={readOnly}
                />
                {tradeDeadline && !readOnly && (
                  <Button variant="ghost" size="sm" onClick={() => setTradeDeadline('')} className="text-xs text-muted-foreground">
                    Clear
                  </Button>
                )}
              </div>
              {tradeDeadline && (
                <p className="text-xs text-muted-foreground">
                  Trades will be locked after <span className="text-foreground font-medium">{format(new Date(tradeDeadline + 'T23:59:59'), 'MMMM d, yyyy')}</span> at 11:59 PM
                </p>
              )}
              {!tradeDeadline && (
                <p className="text-xs text-muted-foreground">No deadline set. Trades allowed all season</p>
              )}
            </CardContent>
          </Card>

          {!readOnly && (
            <Button onClick={saveLeagueSettings} disabled={saving} className="w-full gap-2">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save League Settings'}
            </Button>
          )}

          {!readOnly && <ScheduleGenerator leagueId={league.id} />}
          {isSuperAdmin && <MockStatHarness leagueId={league.id} />}
          {isSuperAdmin && <ResetLeague leagueId={league.id} />}
      </div>}

      {/* ===== ROSTER LIMITS ===== */}
      {activeSection === 'roster' && <div className="space-y-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-primary">Roster Configuration</p>
                <p className="text-muted-foreground">
                  Set the <strong>total roster size</strong> (how many players each team can carry) and 
                  <strong> per-position maximums</strong> (the most of each position a team can have, they don't have to fill them all).
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Total Roster Size */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Total Roster Size
              </CardTitle>
              <CardDescription>
                Maximum number of players each team can carry at any time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 w-10 p-0 text-lg"
                  disabled={readOnly || totalRosterSize <= 10}
                  onClick={() => setTotalRosterSize(prev => Math.max(10, prev - 1))}
                >
                  −
                </Button>
                <span className="text-3xl font-mono font-bold text-primary w-16 text-center">
                  {totalRosterSize}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 w-10 p-0 text-lg"
                  disabled={readOnly || totalRosterSize >= 40}
                  onClick={() => setTotalRosterSize(prev => Math.min(40, prev + 1))}
                >
                  +
                </Button>
                <span className="text-sm text-muted-foreground ml-2">players per team</span>
              </div>
            </CardContent>
          </Card>

          {/* Lineup Lock Type */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <LockIcon className="w-5 h-5 text-primary" />
                Lineup Lock Mode
              </CardTitle>
              <CardDescription>
                Controls when players become locked in lineups each week
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { id: 'weekly' as const, name: 'Weekly Lock', desc: 'All lineups lock when the first game of the matchup week starts', icon: '🔒' },
                { id: 'per_game' as const, name: 'Per-Game Lock', desc: 'Each player locks individually when their real MLB game starts. Unlocked players can still be swapped', icon: '⏱️' },
              ].map((mode) => (
                <button
                  key={mode.id}
                  onClick={() => !readOnly && setLineupLockType(mode.id)}
                  disabled={readOnly}
                  className={cn(
                    "w-full p-3 rounded-lg border text-left transition-all flex items-center justify-between",
                    lineupLockType === mode.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{mode.icon}</span>
                    <div>
                      <p className="font-medium text-sm">{mode.name}</p>
                      <p className="text-xs text-muted-foreground">{mode.desc}</p>
                    </div>
                  </div>
                  {lineupLockType === mode.id && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </CardContent>
          </Card>

          {/* Roster Configuration */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-primary" />
                Roster Configuration
              </CardTitle>
              <CardDescription>
                Configure starting lineup slots and maximum roster limits per position
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Pitcher Slot Type Toggle */}
              <div className="mb-4 space-y-2">
                <Label className="text-sm font-semibold">Pitcher Slots</Label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'split' as const, label: 'Split (SP/RP)', desc: 'Separate starter & reliever slots' },
                    { id: 'combined' as const, label: 'Combined (P)', desc: 'All pitchers share generic P slots' },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => {
                        if (readOnly || pitcherSlotType === opt.id) return;
                        if (opt.id === 'combined') {
                          // Split → Combined: sum SP+RP into P, zero out SP/RP
                          const spVal = rosterLimits['SP'] || 0;
                          const rpVal = rosterLimits['RP'] || 0;
                          setRosterLimits(prev => ({ ...prev, P: spVal + rpVal, SP: 0, RP: 0 }));
                          setRosterMins(prev => ({ ...prev, P: (prev['SP'] || 0) + (prev['RP'] || 0), SP: 0, RP: 0 }));
                        } else {
                          // Combined → Split: zero out P, default SP/RP to 0
                          setRosterLimits(prev => ({ ...prev, SP: 0, RP: 0, P: 0 }));
                          setRosterMins(prev => ({ ...prev, SP: 0, RP: 0, P: 0 }));
                        }
                        setPitcherSlotType(opt.id);
                      }}
                      disabled={readOnly}
                      className={cn(
                        "p-2.5 rounded-lg border text-left transition-all",
                        pitcherSlotType === opt.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                      )}
                    >
                      <p className="font-medium text-xs">{opt.label}</p>
                      <p className="text-[10px] text-muted-foreground">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Tabs defaultValue="starting">
                <TabsList className="w-full mb-4">
                  <TabsTrigger value="starting" className="flex-1 text-xs">Starting Slots</TabsTrigger>
                  <TabsTrigger value="max" className="flex-1 text-xs">Max Roster Limits</TabsTrigger>
                </TabsList>

                {/* Tab 1: Starting Slots */}
                <TabsContent value="starting" className="space-y-1 mt-0">
                  {/* Total Roster Size */}
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 mb-2">
                    <div>
                      <p className="font-semibold text-sm">Players per team</p>
                      <p className="text-[10px] text-muted-foreground">Bench spots auto-calculate from this</p>
                    </div>
                    {readOnly ? (
                      <span className="font-mono font-bold text-sm">{totalRosterSize}</span>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-xs" disabled={totalRosterSize <= 10}
                          onClick={() => setTotalRosterSize(prev => Math.max(10, prev - 1))}>−</Button>
                        <span className="w-8 text-center font-mono font-bold text-sm">{totalRosterSize}</span>
                        <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-xs" disabled={totalRosterSize >= 40}
                          onClick={() => setTotalRosterSize(prev => Math.min(40, prev + 1))}>+</Button>
                      </div>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground mb-3">Number of each position in the active starting lineup</p>
                  {[
                    { slot: 'C', label: 'Catchers (C)', icon: '🥎' },
                    { slot: '1B', label: 'First Basemen (1B)', icon: '🥇' },
                    { slot: '2B', label: 'Second Basemen (2B)', icon: '🥈' },
                    { slot: '3B', label: 'Third Basemen (3B)', icon: '🥉' },
                    { slot: 'SS', label: 'Shortstops (SS)', icon: '⚡' },
                    { slot: 'OF', label: 'Outfielders (OF)', icon: '🏃' },
                    ...(pitcherSlotType === 'combined'
                      ? [{ slot: 'P', label: 'Pitchers (P)', icon: '⚾' }]
                      : [
                          { slot: 'SP', label: 'Starting Pitchers (SP)', icon: '🔥' },
                          { slot: 'RP', label: 'Relief Pitchers (RP)', icon: '💨' },
                        ]),
                    { slot: 'DH', label: 'Designated Hitters (DH)', icon: '💪' },
                    { slot: 'UTIL', label: 'Utility (UTIL)', icon: '🔄' },
                  ].map(pos => {
                    const val = rosterMins[pos.slot] ?? 0;
                    return (
                      <div key={pos.slot} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/30 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{pos.icon}</span>
                          <p className="font-medium text-sm">{pos.label}</p>
                        </div>
                        {readOnly ? (
                          <span className="font-mono font-bold text-sm">{val}</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-xs"
                              disabled={val <= 0}
                              onClick={() => setRosterMins(prev => ({
                                ...prev,
                                [pos.slot]: Math.max(0, (prev[pos.slot] ?? 0) - 1),
                              }))}
                            >
                              −
                            </Button>
                            <span className="w-6 text-center font-mono font-bold text-xs">{val}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-xs"
                              disabled={val >= 10}
                              onClick={() => setRosterMins(prev => ({
                                ...prev,
                                [pos.slot]: Math.min(10, (prev[pos.slot] ?? 0) + 1),
                              }))}
                            >
                              +
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Bench Spots (auto-calculated) */}
                  {(() => {
                    // Only count slots relevant to current pitcher mode
                    const excludeSlots = pitcherSlotType === 'combined' ? ['BENCH', 'SP', 'RP'] : ['BENCH', 'P'];
                    const activeStarters = Object.entries(rosterMins)
                      .filter(([k]) => !excludeSlots.includes(k))
                      .reduce((sum, [, v]) => sum + (v ?? 0), 0);
                    const benchSpots = totalRosterSize - activeStarters;
                    const overflowed = benchSpots < 0;
                    return (
                      <>
                        <div className={cn(
                          "flex items-center justify-between p-3 rounded-lg border",
                          overflowed ? "bg-destructive/10 border-destructive/30" : "bg-muted/20 border-border/50"
                        )}>
                          <div className="flex items-center gap-3">
                            <span className="text-lg">🪑</span>
                            <div>
                              <p className="font-medium text-sm">Bench Spots</p>
                              <p className="text-[10px] text-muted-foreground">{totalRosterSize} total − {activeStarters} starters</p>
                            </div>
                          </div>
                          <span className={cn("font-mono font-bold text-sm", overflowed && "text-destructive")}>{Math.max(0, benchSpots)}</span>
                        </div>
                        {overflowed && (
                          <p className="text-[11px] text-destructive font-medium">
                            Starters ({activeStarters}) exceed roster size ({totalRosterSize}). Increase roster size or reduce starting slots.
                          </p>
                        )}
                        <div className="text-xs text-muted-foreground pt-2 border-t border-border/50">
                          <span className="font-bold text-foreground">{activeStarters}</span> starters + <span className="font-bold text-foreground">{Math.max(0, benchSpots)}</span> bench = <span className="font-bold text-foreground">{totalRosterSize}</span> total roster
                        </div>
                      </>
                    );
                  })()}
                </TabsContent>

                {/* Tab 2: Max Roster Limits */}
                <TabsContent value="max" className="space-y-1 mt-0">
                  <p className="text-xs text-muted-foreground mb-3">Maximum number of each position a team can roster</p>
                  {[
                    { slot: 'C', label: 'Catchers (C)', icon: '🥎' },
                    { slot: '1B', label: 'First Basemen (1B)', icon: '🥇' },
                    { slot: '2B', label: 'Second Basemen (2B)', icon: '🥈' },
                    { slot: '3B', label: 'Third Basemen (3B)', icon: '🥉' },
                    { slot: 'SS', label: 'Shortstops (SS)', icon: '⚡' },
                    { slot: 'OF', label: 'Outfielders (OF)', icon: '🏃' },
                    ...(pitcherSlotType === 'combined'
                      ? [{ slot: 'P', label: 'Pitchers (P)', icon: '⚾' }]
                      : [
                          { slot: 'SP', label: 'Starting Pitchers (SP)', icon: '🔥' },
                          { slot: 'RP', label: 'Relief Pitchers (RP)', icon: '💨' },
                        ]),
                    { slot: 'DH', label: 'Designated Hitters (DH)', icon: '💪' },
                  ].map(pos => {
                    const max = rosterLimits[pos.slot] ?? 0;
                    const posMax = ['P', 'SP', 'RP'].includes(pos.slot) ? 25 : 10;
                    return (
                      <div
                        key={pos.slot}
                        className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{pos.icon}</span>
                          <p className="font-medium text-sm">{pos.label}</p>
                        </div>
                        {readOnly ? (
                          <span className="font-mono font-bold text-sm">{max}</span>
                        ) : (
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-xs"
                              disabled={max <= 0}
                              onClick={() => setRosterLimits(prev => ({
                                ...prev,
                                [pos.slot]: Math.max(0, (prev[pos.slot] ?? 0) - 1),
                              }))}
                            >
                              −
                            </Button>
                            <span className="w-6 text-center font-mono font-bold text-xs">{max}</span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-xs"
                              disabled={max >= posMax}
                              onClick={() => setRosterLimits(prev => ({
                                ...prev,
                                [pos.slot]: Math.min(posMax, (prev[pos.slot] ?? 0) + 1),
                              }))}
                            >
                              +
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {!readOnly && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => {
                  setRosterLimits({ ...DEFAULT_ROSTER_LIMITS });
                  setRosterMins({ ...DEFAULT_ROSTER_MINS });
                  setTotalRosterSize(DEFAULT_ROSTER_SIZE);
                }}
              >
                Reset to Defaults
              </Button>
              <Button onClick={saveLeagueSettings} disabled={saving} className="flex-1 gap-2">
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Roster Settings'}
              </Button>
            </div>
          )}
      </div>}

      {/* ===== SCORING SETTINGS ===== */}
      {activeSection === 'scoring' && <div className="space-y-4">
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-4 flex items-start gap-3">
              <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-primary">Trash Scoring Mode</p>
                <p className="text-muted-foreground">
                  Points are awarded for traditionally "bad" plays. Strikeouts, errors, walks, and blown saves all earn points.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label>Scoring Type</Label>
            <div className="grid grid-cols-1 gap-3">
              {[
                { id: 'trash', name: 'Trash Points', desc: 'Score points for errors, strikeouts, and mistakes', icon: '🗑️' },
                { id: 'standard', name: 'Standard', desc: 'Traditional fantasy scoring', icon: '📊' },
              ].map(t => (
                <button
                  key={t.id}
                  onClick={() => !readOnly && setScoringType(t.id)}
                  disabled={readOnly}
                  className={cn(
                    "p-3 rounded-lg border text-left transition-all flex items-center justify-between",
                    scoringType === t.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{t.icon}</span>
                    <div>
                      <p className="font-medium text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.desc}</p>
                    </div>
                  </div>
                  {scoringType === t.id && <Check className="w-4 h-4 text-primary" />}
                </button>
              ))}
            </div>
          </div>

          {/* Scoring Rules by Category */}
          {MLB_SCORING_CATEGORIES.map(cat => {
            const catRules = scoringRules.filter(r => r.category === cat.id && !r.isNegative);
            const antiRules = scoringRules.filter(r => r.category === cat.id && r.isNegative);
            if (catRules.length === 0 && antiRules.length === 0) return null;
            const enabledCount = [...catRules, ...antiRules].filter(r => r.isEnabled).length;
            const totalCount = catRules.length + antiRules.length;

            return (
              <Card key={cat.id}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <span>{cat.icon}</span> {cat.label}
                    </CardTitle>
                    <Badge variant="secondary" className="text-xs">{enabledCount}/{totalCount}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {catRules.length > 0 && (
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 pt-1 pb-0.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500" /> Trash Stats (Reward Bad)
                    </p>
                  )}
                  {catRules.map(rule => (
                    <div
                      key={rule.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg transition-colors",
                        rule.isEnabled ? "bg-muted/30" : "bg-muted/10 opacity-60"
                      )}
                    >
                      <Switch
                        checked={rule.isEnabled}
                        disabled={readOnly}
                        onCheckedChange={(checked) => {
                          setScoringRules(prev => prev.map(r =>
                            r.id === rule.id ? { ...r, isEnabled: checked } : r
                          ));
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm">{rule.label}</span>
                        <p className="text-xs text-muted-foreground truncate">{rule.description}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-bold text-green-500">+</span>
                        <Input
                          type="number"
                          step="0.5"
                          value={rule.pointValue}
                          onChange={(e) => {
                            setScoringRules(prev => prev.map(r =>
                              r.id === rule.id ? { ...r, pointValue: parseFloat(e.target.value) || 0 } : r
                            ));
                          }}
                          className="w-16 h-7 text-xs text-center font-mono font-bold text-primary"
                          disabled={!rule.isEnabled || readOnly}
                        />
                        <span className="text-xs text-muted-foreground">pts</span>
                      </div>
                    </div>
                  ))}

                  {antiRules.length > 0 && (
                    <>
                      <Separator className="my-2" />
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5 pt-1 pb-0.5">
                        <span className="inline-block w-2 h-2 rounded-full bg-destructive" /> Anti-Stats (Punish Good)
                      </p>
                    </>
                  )}
                  {antiRules.map(rule => (
                    <div
                      key={rule.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg transition-colors border",
                        rule.isEnabled
                          ? "bg-destructive/5 border-destructive/20"
                          : "bg-muted/10 border-transparent opacity-60"
                      )}
                    >
                      <Switch
                        checked={rule.isEnabled}
                        disabled={readOnly}
                        onCheckedChange={(checked) => {
                          setScoringRules(prev => prev.map(r =>
                            r.id === rule.id ? { ...r, isEnabled: checked } : r
                          ));
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-sm">{rule.label}</span>
                        <p className="text-xs text-muted-foreground truncate">{rule.description}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.5"
                          value={rule.pointValue}
                          onChange={(e) => {
                            setScoringRules(prev => prev.map(r =>
                              r.id === rule.id ? { ...r, pointValue: parseFloat(e.target.value) || 0 } : r
                            ));
                          }}
                          className="w-16 h-7 text-xs text-center font-mono font-bold text-destructive"
                          disabled={!rule.isEnabled || readOnly}
                        />
                        <span className="text-xs text-muted-foreground">pts</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}

          {!readOnly && (
            <Button onClick={saveLeagueSettings} disabled={saving} className="w-full gap-2">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Scoring Settings'}
            </Button>
          )}
      </div>}

      {/* ===== SABOTAGE SETTINGS ===== */}
      {activeSection === 'sabotages' && <div className="space-y-4">
          <Tabs defaultValue="matchup" className="space-y-4">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="matchup" className="text-xs sm:text-sm gap-1">
                <Bomb className="w-3.5 h-3.5 hidden sm:inline" />
                Matchup
              </TabsTrigger>
              <TabsTrigger value="draft" className="text-xs sm:text-sm gap-1">
                <Gavel className="w-3.5 h-3.5 hidden sm:inline" />
                Draft
              </TabsTrigger>
            </TabsList>

            {/* Matchup Sabotages */}
            <TabsContent value="matchup" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Bomb className="w-5 h-5 text-primary" />
                    Matchup Sabotage Rules
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium text-sm">Sabotages Enabled</p>
                      <p className="text-xs text-muted-foreground">Allow mid-matchup sabotage wheel spins</p>
                    </div>
                    <Switch checked={sabotageEnabled} onCheckedChange={setSabotageEnabled} disabled={readOnly} />
                  </div>

                  {sabotageEnabled && (
                    <>
                      <div className="space-y-2">
                        <Label>Spin Cost</Label>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4 text-muted-foreground" />
                          <Input
                            type="number"
                            value={sabotageSpinCost}
                            onChange={(e) => setSabotageSpinCost(parseInt(e.target.value) || 10)}
                            className="w-24"
                            min={1}
                            max={100}
                            disabled={readOnly}
                          />
                          <span className="text-xs text-muted-foreground">per spin</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                        <div>
                          <p className="font-medium text-sm">Limit spins per matchup</p>
                          <p className="text-xs text-muted-foreground">Cap how many times each team can spin per matchup</p>
                        </div>
                        <Switch checked={limitSpinsPerMatchup} onCheckedChange={setLimitSpinsPerMatchup} disabled={readOnly} />
                      </div>

                      {limitSpinsPerMatchup && (
                        <div className="space-y-2 pl-4 border-l-2 border-primary/20">
                          <Label>Max spins per matchup</Label>
                          <div className="flex items-center gap-1">
                            {[1, 2, 3, 4, 5].map(n => (
                              <Button
                                key={n}
                                variant={maxSpinsPerMatchup === n ? 'default' : 'outline'}
                                size="sm"
                                className="h-8 w-8 p-0 text-xs"
                                disabled={readOnly}
                                onClick={() => setMaxSpinsPerMatchup(n)}
                              >
                                {n}
                              </Button>
                            ))}
                          </div>
                          <p className="text-[10px] text-muted-foreground">Each team can spin the wheel up to {maxSpinsPerMatchup} time{maxSpinsPerMatchup > 1 ? 's' : ''} per matchup</p>
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              {sabotageEnabled && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base text-primary">Sabotage Cards</CardTitle>
                    <p className="text-xs text-muted-foreground">All 10 matchup sabotages are active for the beta.</p>
                  </CardHeader>
                  <CardContent className="space-y-1.5">
                    {[
                      { icon: '⚾', name: 'Balk Call', desc: '2× BB/BK/HBP · Both teams' },
                      { icon: '🧤', name: 'Butterfingers', desc: '1.5× errors · Both teams' },
                      { icon: '🏃', name: "Fielder's Choice", desc: 'SB nullified · Your team' },
                      { icon: '🔄', name: 'Pitching Change', desc: 'Best→worst pitcher · Opponent' },
                      { icon: '💀', name: 'K-Pocalypse', desc: '2× strikeouts · Both teams' },
                      { icon: '🏠', name: 'Gopher Ball', desc: '2× HR allowed · Both teams' },
                      { icon: '💉', name: 'Juiced Balls', desc: '50% less hit penalty · Your team' },
                      { icon: '7️⃣', name: 'Seventh Inning Curse', desc: '2× earned runs · League-wide' },
                      { icon: '🎰', name: 'Error Jackpot', desc: '3× errors · League-wide' },
                      { icon: '🚪', name: 'Closer Curse', desc: '3× blown saves · League-wide' },
                    ].map(sab => (
                      <div key={sab.name} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/30">
                        <span className="text-lg">{sab.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm">{sab.name}</p>
                        </div>
                        <Badge variant="outline" className="text-[10px] shrink-0">{sab.desc}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {!readOnly && (
                <Button onClick={saveLeagueSettings} disabled={saving} className="w-full gap-2">
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : 'Save Matchup Sabotage Settings'}
                </Button>
              )}
            </TabsContent>

            {/* Draft Sabotages */}
            <TabsContent value="draft" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Gavel className="w-5 h-5 text-primary" />
                    Draft Sabotage Rules
                  </CardTitle>
                  <CardDescription>Sabotages available during the draft, priced by severity</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                    <div>
                      <p className="font-medium text-sm">Draft Sabotages Enabled</p>
                      <p className="text-xs text-muted-foreground">Allow sabotages during the draft</p>
                    </div>
                    <Switch checked={draftSabotageEnabled} onCheckedChange={setDraftSabotageEnabled} disabled={readOnly} />
                  </div>
                </CardContent>
              </Card>

              {draftSabotageEnabled && (
                <DraftSabotageSettings readOnly={readOnly} />
              )}
            </TabsContent>
          </Tabs>
      </div>}

      {/* ===== DRAFT SETTINGS ===== */}
      {activeSection === 'draft' && <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Gavel className="w-5 h-5 text-primary" />
                Draft Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Draft Type</Label>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    { id: 'snake', name: 'Snake', desc: 'Order reverses each round' },
                    { id: 'linear', name: 'Linear', desc: 'Same order every round' },
                    { id: 'slow', name: 'Slow Draft', desc: 'Hours between picks' },
                  ].map(type => (
                    <button
                      key={type.id}
                      onClick={() => !readOnly && setDraftType(type.id)}
                      disabled={readOnly}
                      className={cn(
                        "p-3 rounded-lg border text-left transition-all",
                        draftType === type.id ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                      )}
                    >
                      <p className="font-medium text-sm">{type.name}</p>
                      <p className="text-xs text-muted-foreground">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <Separator />

              <DraftTimingSection
                draftType={draftType}
                secondsPerPick={secondsPerPick}
                setSecondsPerPick={setSecondsPerPick}
                hoursPerPick={hoursPerPick}
                setHoursPerPick={setHoursPerPick}
                pauseOvernight={pauseOvernight}
                setPauseOvernight={setPauseOvernight}
                pauseStartHour={pauseStartHour}
                setPauseStartHour={setPauseStartHour}
                pauseEndHour={pauseEndHour}
                setPauseEndHour={setPauseEndHour}
              />
            </CardContent>
          </Card>

          <DraftScheduleSection leagueId={league.id} draftType={draftType} readOnly={readOnly} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Draft Order</CardTitle>
              <CardDescription>Team order will be randomized before the draft begins</CardDescription>
            </CardHeader>
            <CardContent>
              <DraftOrderPreview leagueId={league.id} />
            </CardContent>
          </Card>


          {!readOnly && (
            <Button onClick={saveLeagueSettings} disabled={saving} className="w-full gap-2">
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save Draft Settings'}
            </Button>
          )}
      </div>}

      {/* ===== PAYMENTS / FUNDING ===== */}
      {activeSection === 'payments' && <div className="space-y-4">
        {readOnly ? <PlayerFundsView teamCount={teamCount} /> : <FundsSection teamCount={teamCount} />}
      </div>}

      {/* ===== MEMBERS ===== */}
      {activeSection === 'members' && <div className="space-y-4">
        <MembersTab isCommissioner={isCommissioner} leagueId={league.id} />
      </div>}

      {/* ===== COMMISSIONER OVERRIDES ===== */}
      {activeSection === 'overrides' && isCommissioner && <div className="space-y-4">
        <CommissionerOverrides leagueId={league.id} />
      </div>}
    </div>
  );
};

// ===== Sub-components =====

interface DraftTimingSectionProps {
  draftType: string;
  secondsPerPick: number;
  setSecondsPerPick: (v: number) => void;
  hoursPerPick: number;
  setHoursPerPick: (v: number) => void;
  pauseOvernight: boolean;
  setPauseOvernight: (v: boolean) => void;
  pauseStartHour: number;
  setPauseStartHour: (v: number) => void;
  pauseEndHour: number;
  setPauseEndHour: (v: number) => void;
}

const DraftTimingSection = ({ draftType, secondsPerPick, setSecondsPerPick, hoursPerPick, setHoursPerPick, pauseOvernight, setPauseOvernight, pauseStartHour, setPauseStartHour, pauseEndHour, setPauseEndHour }: DraftTimingSectionProps) => {

  return (
    <div className="space-y-4">
      {draftType !== 'slow' ? (
        <div className="space-y-2">
          <Label>Seconds Per Pick</Label>
          <Select value={secondsPerPick.toString()} onValueChange={(v) => setSecondsPerPick(parseInt(v))}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {[30, 60, 90, 120, 150, 180, 0].map(s => (
                <SelectItem key={s} value={s.toString()}>
                  {s === 0 ? 'Unlimited' : `${s} seconds`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>Hours Per Pick</Label>
            <Select value={hoursPerPick.toString()} onValueChange={(v) => setHoursPerPick(parseInt(v))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 4, 6, 8, 12, 24, 48].map(h => (
                  <SelectItem key={h} value={h.toString()}>{h} {h === 1 ? 'hour' : 'hours'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
            <div>
              <p className="font-medium text-sm">Pause Overnight</p>
              <p className="text-xs text-muted-foreground">Clock stops during sleeping hours</p>
            </div>
            <Switch checked={pauseOvernight} onCheckedChange={setPauseOvernight} />
          </div>

          {pauseOvernight && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Pause At</Label>
                <Select value={pauseStartHour.toString()} onValueChange={(v) => setPauseStartHour(parseInt(v))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={i.toString()}>
                        {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Resume At</Label>
                <Select value={pauseEndHour.toString()} onValueChange={(v) => setPauseEndHour(parseInt(v))}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, i) => (
                      <SelectItem key={i} value={i.toString()}>
                        {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </>
      )}

      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
        <div>
          <p className="font-medium text-sm">Auto-Pick on Timeout</p>
          <p className="text-xs text-muted-foreground">Auto-draft if timer expires</p>
        </div>
        <Switch defaultChecked />
      </div>

      <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
        <div>
          <p className="font-medium text-sm">Allow Draft Trades</p>
          <p className="text-xs text-muted-foreground">Teams can trade picks during the draft</p>
        </div>
        <Switch defaultChecked={false} />
      </div>
    </div>
  );
};

const DraftScheduleSection = ({ leagueId, draftType, readOnly }: { leagueId: string; draftType: string; readOnly: boolean }) => {
  const [scheduledDate, setScheduledDate] = useState<Date | undefined>(undefined);
  const [scheduledTime, setScheduledTime] = useState('19:00');
  const [isScheduling, setIsScheduling] = useState(false);
  const [existingSchedule, setExistingSchedule] = useState<string | null>(null);
  const { toast } = useToast();

  // Load existing schedule
  useEffect(() => {
    const fetchSchedule = async () => {
      const { data } = await supabase
        .from('drafts')
        .select('scheduled_for, status')
        .eq('league_id', leagueId)
        .maybeSingle();
      if (data?.scheduled_for) {
        setExistingSchedule(data.scheduled_for);
        const d = new Date(data.scheduled_for);
        setScheduledDate(d);
        setScheduledTime(`${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`);
      }
    };
    fetchSchedule();
  }, [leagueId]);

  const handleSchedule = async () => {
    if (!scheduledDate) return;
    setIsScheduling(true);
    try {
      const [hours, minutes] = scheduledTime.split(':').map(Number);
      const scheduledFor = new Date(scheduledDate);
      scheduledFor.setHours(hours, minutes, 0, 0);

      if (scheduledFor <= new Date()) {
        toast({ title: 'Invalid date', description: 'Scheduled time must be in the future.', variant: 'destructive' });
        setIsScheduling(false);
        return;
      }

      // Check for existing draft
      const { data: existingDraft } = await supabase
        .from('drafts')
        .select('id')
        .eq('league_id', leagueId)
        .maybeSingle();

      if (existingDraft) {
        await supabase
          .from('drafts')
          .update({
            scheduled_for: scheduledFor.toISOString(),
            draft_type: draftType,
            status: 'scheduled',
          } as any)
          .eq('id', existingDraft.id);
      } else {
        await supabase
          .from('drafts')
          .insert({
            league_id: leagueId,
            scheduled_for: scheduledFor.toISOString(),
            draft_type: draftType,
            status: 'scheduled',
          } as any);
      }

      await supabase
        .from('leagues')
        .update({ status: 'pre_draft' })
        .eq('id', leagueId);

      setExistingSchedule(scheduledFor.toISOString());
      toast({ title: 'Draft Scheduled!', description: `Draft set for ${format(scheduledFor, 'PPP')} at ${format(scheduledFor, 'p')}` });
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to schedule draft', variant: 'destructive' });
    } finally {
      setIsScheduling(false);
    }
  };

  if (readOnly) {
    if (!existingSchedule) return null;
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-primary" />
            <div>
              <p className="font-medium text-sm">Draft Scheduled</p>
              <p className="text-sm text-muted-foreground">
                {format(new Date(existingSchedule), 'EEEE, MMMM d, yyyy')} at {format(new Date(existingSchedule), 'p')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Clock className="w-4 h-4 text-primary" />
          Schedule Draft
        </CardTitle>
        <CardDescription>Set when the {draftType === 'slow' ? 'slow draft' : 'draft'} begins</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Draft Date</Label>
            <input
              type="date"
              value={scheduledDate ? `${scheduledDate.getFullYear()}-${String(scheduledDate.getMonth() + 1).padStart(2, '0')}-${String(scheduledDate.getDate()).padStart(2, '0')}` : ''}
              onChange={(e) => {
                if (e.target.value) {
                  const [y, m, d] = e.target.value.split('-').map(Number);
                  setScheduledDate(new Date(y, m - 1, d));
                } else {
                  setScheduledDate(undefined);
                }
              }}
              style={{ colorScheme: 'dark' }}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label>Draft Time</Label>
            <Input
              type="time"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {existingSchedule && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
            <p className="text-foreground">
              Currently scheduled: <strong>{format(new Date(existingSchedule), 'EEEE, MMMM d, yyyy')}</strong> at <strong>{format(new Date(existingSchedule), 'p')}</strong>
            </p>
          </div>
        )}

        <Button
          onClick={handleSchedule}
          disabled={isScheduling || !scheduledDate}
          variant="outline"
          className="w-full gap-2"
        >
          {isScheduling ? 'Scheduling...' : existingSchedule ? 'Update Schedule' : 'Schedule Draft'}
        </Button>
      </CardContent>
    </Card>
  );
};

const DraftOrderPreview = ({ leagueId }: { leagueId: string }) => {
  const [teams, setTeams] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    const fetchTeams = async () => {
      const { data } = await supabase
        .from('teams')
        .select('id, name')
        .eq('league_id', leagueId)
        .order('created_at');
      setTeams(data || []);
    };
    fetchTeams();
  }, [leagueId]);

  if (teams.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-4">No teams joined yet.</p>;
  }

  return (
    <div className="space-y-1">
      {teams.map((team, i) => (
        <div key={team.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors">
          <Badge variant="outline" className="text-xs w-8 justify-center font-mono">{i + 1}</Badge>
          <span className="text-sm font-medium">{team.name}</span>
        </div>
      ))}
    </div>
  );
};

const FundsSection = ({ teamCount }: { teamCount: number }) => {
  const [buyIn] = useState(100);
  const [feeOption, setFeeOption] = useState<'split' | 'commissioner' | 'none'>('split');
  const LEAGUE_FEE = 99;
  const feePerTeam = LEAGUE_FEE / teamCount;

  const paidCount = 6;
  const pendingCount = 1;
  const overdueCount = 1;
  const collected = paidCount * (buyIn + (feeOption === 'split' ? feePerTeam : 0));
  const totalExpected = teamCount * (buyIn + (feeOption === 'split' ? feePerTeam : 0));

  return (
    <div className="space-y-4">
      {/* Pool Overview */}
      <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Wallet className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-muted-foreground">Funds Collected</span>
              </div>
              <p className="text-3xl font-bold font-heading text-primary">
                ${collected.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground">
                of ${totalExpected.toLocaleString(undefined, { minimumFractionDigits: 2 })} expected
              </p>
            </div>
            <Badge variant="outline" className="gap-1 border-primary/50 text-primary">
              <ShieldCheck className="w-3 h-3" />
              Secure
            </Badge>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="p-2 rounded-lg bg-background/50 text-center">
              <CheckCircle2 className="w-4 h-4 text-primary mx-auto mb-1" />
              <p className="font-bold text-primary">{paidCount}</p>
              <p className="text-[10px] text-muted-foreground">Paid</p>
            </div>
            <div className="p-2 rounded-lg bg-background/50 text-center">
              <Clock className="w-4 h-4 text-accent-foreground mx-auto mb-1" />
              <p className="font-bold text-accent-foreground">{pendingCount}</p>
              <p className="text-[10px] text-muted-foreground">Pending</p>
            </div>
            <div className="p-2 rounded-lg bg-background/50 text-center">
              <AlertCircle className="w-4 h-4 text-destructive mx-auto mb-1" />
              <p className="font-bold text-destructive">{overdueCount}</p>
              <p className="text-[10px] text-muted-foreground">Overdue</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* League Fee Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            League Operating Fee ($99)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <RadioGroup value={feeOption} onValueChange={(v) => setFeeOption(v as any)} className="space-y-2">
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/30">
              <RadioGroupItem value="split" id="split" />
              <Label htmlFor="split" className="flex-1 cursor-pointer">
                <span className="font-medium text-sm">Split across all teams</span>
                <p className="text-xs text-muted-foreground">${feePerTeam.toFixed(2)} per team</p>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/30">
              <RadioGroupItem value="commissioner" id="comm" />
              <Label htmlFor="comm" className="flex-1 cursor-pointer">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">Commissioner pays</span>
                  <Crown className="w-3.5 h-3.5 text-primary" />
                </div>
                <p className="text-xs text-muted-foreground">Commissioner covers full $99</p>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-lg bg-muted/30">
              <RadioGroupItem value="none" id="none" />
              <Label htmlFor="none" className="flex-1 cursor-pointer">
                <span className="font-medium text-sm">No league fee</span>
                <p className="text-xs text-muted-foreground">Buy-in only</p>
              </Label>
            </div>
          </RadioGroup>
        </CardContent>
      </Card>

      {/* Buy-In Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Buy-In Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">League Buy-In</span>
              <span className="font-bold">${buyIn}</span>
            </div>
            {feeOption !== 'none' && (
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Operating Fee {feeOption === 'split' ? `(÷ ${teamCount})` : '(Commissioner)'}
                </span>
                <span className="font-bold">
                  ${feeOption === 'split' ? feePerTeam.toFixed(2) : LEAGUE_FEE}
                </span>
              </div>
            )}
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="font-bold">Total per Team</span>
              <span className="font-bold text-primary">
                ${(buyIn + (feeOption === 'split' ? feePerTeam : 0)).toFixed(2)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const PlayerFundsView = ({ teamCount }: { teamCount: number }) => {
  const buyIn = 100;
  const sabotageBalance = 100; // Would come from team data in production

  return (
    <div className="space-y-4">
      <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-5 h-5 text-primary" />
            <span className="text-sm font-medium text-muted-foreground">My Balance</span>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="p-3 rounded-lg bg-background/50 text-center">
              <p className="text-2xl font-bold text-primary">${buyIn}</p>
              <p className="text-xs text-muted-foreground">Buy-In Paid</p>
            </div>
            <div className="p-3 rounded-lg bg-background/50 text-center">
              <p className="text-2xl font-bold text-foreground">{sabotageBalance}</p>
              <p className="text-xs text-muted-foreground">Sabotage Credits</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">League Pool</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Buy-In per Team</span>
              <span className="font-bold">${buyIn}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Teams</span>
              <span className="font-bold">{teamCount}</span>
            </div>
            <Separator />
            <div className="flex justify-between text-sm">
              <span className="font-bold">Total Prize Pool</span>
              <span className="font-bold text-primary">${(buyIn * teamCount).toLocaleString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Schedule Generator component
const ScheduleGenerator = ({ leagueId }: { leagueId: string }) => {
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const { toast } = useToast();

  const generateSchedule = async () => {
    setGenerating(true);
    setResult(null);
    try {
      const { data: { session } } = await supabase.auth.refreshSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/matchup-engine?action=schedule`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ league_id: leagueId }),
        }
      );
      const data = await res.json();
      console.log('[Schedule] response:', res.status, data);
      if (!res.ok) throw new Error(data.error || `Failed to generate schedule (${res.status})`);

      let msg: string;
      if (data.existing) {
        msg = `Schedule already exists`;
      } else {
        msg = `Created ${data.matchups_created} matchups over ${data.weeks} weeks`;
      }
      setResult(msg);
      toast({ title: 'Schedule generated!', description: msg });
    } catch (err: any) {
      console.error('[Schedule] generation error:', err);
      setResult(`Error: ${err.message}`);
      toast({ title: 'Schedule generation failed', description: err.message, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Calendar className="w-4 h-4" />
          Season Schedule
        </CardTitle>
        <CardDescription>Generate the weekly H2H matchup schedule</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button onClick={generateSchedule} disabled={generating} className="w-full gap-2">
          <Calendar className="w-4 h-4" />
          {generating ? 'Generating...' : 'Generate Matchup Schedule'}
        </Button>
        {result && (
          <p className="text-sm text-muted-foreground text-center">{result}</p>
        )}
      </CardContent>
    </Card>
  );
};

// Mock Stat Injection Harness (admin/commissioner tool for off-season testing)
const MockStatHarness = ({ leagueId }: { leagueId: string }) => {
  const [injecting, setInjecting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [gamesPerDay, setGamesPerDay] = useState(2);
  const [triggerScoring, setTriggerScoring] = useState(true);
  const { toast } = useToast();

  const injectStats = async () => {
    setInjecting(true);
    setResult(null);
    try {
      const { data: { session } } = await supabase.auth.refreshSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/inject-mock-stats`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            league_id: leagueId,
            start_date: startDate,
            end_date: endDate,
            games_per_day: gamesPerDay,
            trigger_scoring: triggerScoring,
          }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Injection failed');

      setResult(data);
      toast({
        title: 'Mock stats injected!',
        description: `${data.stats_injected} stat rows across ${data.days} days`,
      });
    } catch (err: any) {
      toast({ title: 'Injection failed', description: err.message, variant: 'destructive' });
    } finally {
      setInjecting(false);
    }
  };

  return (
    <Card className="border-amber-500/30 bg-amber-500/5">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="w-4 h-4 text-amber-500" />
          Mock Stat Harness
        </CardTitle>
        <CardDescription>Inject fake game stats for off-season testing</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Start Date</Label>
            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End Date</Label>
            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Games per Day</Label>
          <Select value={gamesPerDay.toString()} onValueChange={v => setGamesPerDay(parseInt(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5].map(n => (
                <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={triggerScoring} onCheckedChange={setTriggerScoring} />
          <Label className="text-xs">Auto-trigger matchup scoring after injection</Label>
        </div>
        <Button
          onClick={injectStats}
          disabled={injecting}
          variant="outline"
          className="w-full gap-2 border-amber-500/40 text-amber-600 hover:bg-amber-500/10"
        >
          <Zap className="w-4 h-4" />
          {injecting ? 'Injecting Stats...' : 'Inject Mock Stats'}
        </Button>
        {result && (
          <div className="text-xs space-y-1 p-3 rounded-lg bg-muted/50">
            <p className="font-medium text-foreground">
              ✅ {result.stats_injected} stat rows injected
            </p>
            <p className="text-muted-foreground">
              {result.days} days • {result.games_per_day} games/day • {result.players_in_pool} players
            </p>
            {result.scoring_result && (
              <p className="text-muted-foreground">
                Scoring: {result.scoring_result.matchups_scored || 0} matchups scored
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Reset League — Danger zone for wiping test data
const ResetLeague = ({ leagueId }: { leagueId: string }) => {
  const [resetting, setResetting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [resetDraft, setResetDraft] = useState(true);
  const [resetRosters, setResetRosters] = useState(true);
  const [resetMatchups, setResetMatchups] = useState(true);
  const [resetSabotages, setResetSabotages] = useState(true);
  const [resetMockStats, setResetMockStats] = useState(true);
  const { toast } = useToast();

  const doReset = async () => {
    setResetting(true);
    setResult(null);
    setShowConfirm(false);
    try {
      const { data: { session } } = await supabase.auth.refreshSession();
      if (!session) throw new Error('Not authenticated');

      const res = await fetch(
        `https://${SUPABASE_PROJECT_ID}.supabase.co/functions/v1/reset-league`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
            'apikey': SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            league_id: leagueId,
            reset_draft: resetDraft,
            reset_rosters: resetRosters,
            reset_matchups: resetMatchups,
            reset_sabotages: resetSabotages,
            reset_mock_stats: resetMockStats,
          }),
        }
      );
      const data = await res.json();
      console.log('[Reset] response:', res.status, data);
      if (!res.ok) throw new Error(data.error || 'Reset failed');

      setResult(data.results);
      toast({ title: 'League reset complete', description: 'Selected data has been wiped.' });
    } catch (err: any) {
      console.error('[Reset] error:', err);
      toast({ title: 'Reset failed', description: err.message, variant: 'destructive' });
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2 text-destructive">
          <AlertCircle className="w-4 h-4" />
          Reset League Data
        </CardTitle>
        <CardDescription>Wipe test data to start fresh. This cannot be undone.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Switch checked={resetDraft} onCheckedChange={setResetDraft} />
            <Label className="text-xs">Draft (picks, order, queues → reset to scheduled)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={resetRosters} onCheckedChange={setResetRosters} />
            <Label className="text-xs">Rosters (all roster assignments)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={resetMatchups} onCheckedChange={setResetMatchups} />
            <Label className="text-xs">Matchups (delete all matchups & team records)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={resetSabotages} onCheckedChange={setResetSabotages} />
            <Label className="text-xs">Sabotages (delete all applied sabotages)</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={resetMockStats} onCheckedChange={setResetMockStats} />
            <Label className="text-xs">Mock stats (game_pk ≥ 900000 only)</Label>
          </div>
        </div>

        {!showConfirm ? (
          <Button
            onClick={() => setShowConfirm(true)}
            variant="destructive"
            className="w-full gap-2"
            disabled={resetting || (!resetDraft && !resetRosters && !resetMatchups && !resetSabotages && !resetMockStats)}
          >
            <AlertCircle className="w-4 h-4" />
            Reset Selected Data
          </Button>
        ) : (
          <div className="space-y-2 p-3 rounded-lg border border-destructive/50 bg-destructive/10">
            <p className="text-xs font-medium text-destructive">
              ⚠️ This will permanently delete the selected data. Are you sure?
            </p>
            <div className="flex gap-2">
              <Button onClick={doReset} variant="destructive" size="sm" disabled={resetting} className="flex-1">
                {resetting ? 'Resetting...' : 'Yes, Reset Now'}
              </Button>
              <Button onClick={() => setShowConfirm(false)} variant="outline" size="sm" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        )}

        {result && (
          <div className="text-xs space-y-1 p-3 rounded-lg bg-muted/50">
            <p className="font-medium text-foreground">✅ Reset complete</p>
            {result.draft && <p className="text-muted-foreground">Draft: {result.draft.picks_deleted} picks deleted</p>}
            {result.rosters_deleted !== undefined && <p className="text-muted-foreground">Rosters: {result.rosters_deleted} assignments removed</p>}
            {result.matchups_deleted !== undefined && <p className="text-muted-foreground">Matchups: {result.matchups_deleted} deleted</p>}
            {result.sabotages_deleted !== undefined && <p className="text-muted-foreground">Sabotages: {result.sabotages_deleted} deleted</p>}
            {result.mock_stats_deleted !== undefined && <p className="text-muted-foreground">Mock stats: {result.mock_stats_deleted} rows deleted, {result.mock_games_deleted} games deleted</p>}
            {result.league_status && <p className="text-muted-foreground">League status → {result.league_status}</p>}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MLBBetaSettings;
